import React from 'react';
import styled from 'styled-components';
import { SubContainer, SubTitle, SubWrapper, Title } from './MemberSetting';
import { useSetting } from '../../shared/hooks/useApi/useSetting';
import { UpdateSettingReq } from '../../shared/dto/ReqDtoRepository';
import { MenuVisible } from '../../shared/dto/EntityRepository';
import MoleculeDivToggle from '../../shared/component/molecule/MoleculeDivToggle';
import SelectBox from '../../shared/component/SelectBox';
import { AlertPosition, AlertType, DeviceType, ExposureLevel, MemberType, ModalType, NavLayout, SelectType } from '../../shared/enum/EnumRepository';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { getAlertMessage, type AlertMessageKey } from '../../shared/constants/alertMessages';
import { getNavLayoutLabel, getPageExposureExplainMessage, getSettingsMessage } from '../../shared/constants/pageMessages';
import MoleculeDivBtn from '../../shared/component/molecule/MoleculeDivBtn';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useDeviceStore } from '../../shared/store/deviceStore';
import { SubValueChangeMobile, SubValue } from './MemberSetting';
import { ENABLE_FIXED_NAV_LAYOUT } from '../../shared/util/navLayout';

const PageSetting: React.FC = () => {
  const { setting, updateSetting } = useSetting();
  const { enqueueAlert } = useStandardAlertStore();
  const { setStandardModal } = useModalStore();
  const { deviceType } = useDeviceStore();

  if (!setting) return null;

  const isGroup = setting.memberType === MemberType.GROUP;
  const isMembersVisible = setting.menuVisible.members !== false;

  const buildUpdateSettingReq = (patch: Partial<UpdateSettingReq>): UpdateSettingReq => ({
    id: setting.id,
    username: patch.username ?? setting.username,
    exposureLevel: patch.exposureLevel ?? setting.exposureLevel,
    menuVisible: patch.menuVisible ?? setting.menuVisible,
    navLayout: patch.navLayout ?? setting.navLayout,
  });

  const handleExposureSelect = (nextLevel: ExposureLevel) => {
    if (nextLevel === setting.exposureLevel) return;

    const confirmKey: AlertMessageKey = (() => {
      switch (nextLevel) {
        case ExposureLevel.PRIVATE:
          return 'CONFIRM_MAKE_PAGE_PRIVATE';
        case ExposureLevel.SEARCHABLE:
          return 'CONFIRM_MAKE_PAGE_SEARCHABLE';
        case ExposureLevel.PUBLIC:
          return 'CONFIRM_MAKE_PAGE_PUBLIC';
        default: {
          const _exhaustive: never = nextLevel;
          return _exhaustive;
        }
      }
    })();

    const message = getAlertMessage(confirmKey);

    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: message,
      callBack: async () => {
        await updateSetting(buildUpdateSettingReq({ exposureLevel: nextLevel }));
      },
    });
  };

  const handleNavLayoutSelect = async (nextLayout: NavLayout) => {
    if (nextLayout === setting.navLayout) return;
    await updateSetting(buildUpdateSettingReq({ navLayout: nextLayout }));
  };

  const handleMenuVisibilityChange = async (field: keyof MenuVisible, value: boolean) => {
    await updateSetting(
      buildUpdateSettingReq({
        menuVisible: {
          ...setting.menuVisible,
          [field]: value,
        },
      })
    );
  };

  const guardSeoEditable = () => {
    if (setting.exposureLevel === ExposureLevel.PRIVATE) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SEO_REQUIRES_SEARCHABLE_ARCHIVE'),
      });
      return false;
    }
    return true;
  };

  const handleChangeShare = () => {
    if (!guardSeoEditable()) return;
    setStandardModal({
      modalType: ModalType.CHANGE_SHARE,
      title: getSettingsMessage('SETTINGS_SHARE'),
      value: "",
      handleChange: () => { }
    });
  }

  const handleChangeSearch = () => {
    if (!guardSeoEditable()) return;
    setStandardModal({
      modalType: ModalType.CHANGE_SEARCH_ENGINE,
      title: getSettingsMessage('SETTINGS_SEARCH'),
      value: "",
      handleChange: () => { }
    });
  }

  const getShareValue = () => {
    if (setting.exposureLevel === ExposureLevel.PRIVATE) {
      return "NONE";
    }

    const toTruncated = (value: string) => {
      const trimmed = value.trim();
      if (trimmed.length === 0) return '-';
      return trimmed.length > 20 ? `${trimmed.slice(0, 20)}...` : trimmed;
    };

    const hasCustomOgImage = setting.seo.uploadFile.originUrl.trim().length > 0;

    return [
      `SubTitle: ${toTruncated(setting.seo.subTitle)}`,
      getSettingsMessage(hasCustomOgImage ? 'SEARCH_PREVIEW_OG_IMAGE_CUSTOM' : 'SEARCH_PREVIEW_OG_IMAGE_DEFAULT'),
    ].join(' · ');
  };

  const getSearchEngineValue = () => {
    if (setting.exposureLevel === ExposureLevel.PRIVATE) {
      return "NONE";
    }

    const toDisplay = (value: string | undefined) => {
      const trimmed = (value ?? '').trim();
      return trimmed.length > 0 ? trimmed : '-';
    };

    const toDescription = (value?: string) => {
      const trimmed = (value ?? '').trim();
      if (trimmed.length === 0) return '-';
      return trimmed.length > 20 ? `${trimmed.slice(0, 20)}...` : trimmed;
    };

    return [
      `Name: ${toDisplay(setting.seo.name)}`,
      `Name(EN): ${toDisplay(setting.seo.nameEn)}`,
      `Description: ${toDescription(setting.seo.description)}`,
      `Description(EN): ${toDescription(setting.seo.descriptionEn)}`,
    ].join(' · ');
  };

  return (
    <PageSettingComp>
      <Title>Page</Title> 
      <SubContainer>
        <SubWrapper>
          <SubTitle>{getSettingsMessage('SETTINGS_PAGE_VISIBILITY')}
            <SubExplain>
              {getPageExposureExplainMessage(setting.exposureLevel)}
            </SubExplain>
          </SubTitle>
          <ExposureRow>
            <ExposureCurrentLabel>{setting.exposureLevel}</ExposureCurrentLabel>
            <ExposureSelectBoxWrap>
              <SelectBox
                value={setting.exposureLevel}
                selectType={SelectType.EXPOSURE_LEVEL}
                direction={false}
                handleChange={handleExposureSelect}
              />
            </ExposureSelectBoxWrap>
          </ExposureRow>
        </SubWrapper>
        <SubWrapper>
          <SubTitle>{getSettingsMessage('SETTINGS_MENU_VISIBILITY')}</SubTitle>
          <MoleculeDivToggle
            value={setting.menuVisible.billboard}
            name={"Billboard"}
            handleToggle={(e) => handleMenuVisibilityChange("billboard", e.target.checked)}
          />
          <MoleculeDivToggle
            value={setting.menuVisible.projects}
            name={"Projects"}
            handleToggle={(e) => handleMenuVisibilityChange("projects", e.target.checked)}
          />
          <MoleculeDivToggle
            value={setting.menuVisible.works}
            name={"Works"}
            handleToggle={(e) => handleMenuVisibilityChange("works", e.target.checked)}
          />
          <MoleculeDivToggle
            value={setting.menuVisible.texts}
            name={"Texts"}
            handleToggle={(e) => handleMenuVisibilityChange("texts", e.target.checked)}
          />
          {isGroup &&
            <MoleculeDivToggle
              value={isMembersVisible}
              name={"Members"}
              handleToggle={(e) => handleMenuVisibilityChange("members", e.target.checked)}
            />
          }
          <MoleculeDivToggle
            value={setting.menuVisible.about}
            name={"About"}
            handleToggle={(e) => handleMenuVisibilityChange("about", e.target.checked)}
          />
          <MoleculeDivToggle
            value={setting.menuVisible.contact}
            name={"Contact"}
            handleToggle={(e) => handleMenuVisibilityChange("contact", e.target.checked)}
          />
        </SubWrapper>
        {ENABLE_FIXED_NAV_LAYOUT && (
          <SubWrapper>
            <SubTitle>{getSettingsMessage('SETTINGS_NAV_LAYOUT')}
              <SubExplain>{getSettingsMessage('NAV_LAYOUT_EXPLAIN')}</SubExplain>
            </SubTitle>
            <ExposureRow>
              <ExposureCurrentLabel>{getNavLayoutLabel(setting.navLayout)}</ExposureCurrentLabel>
              <NavLayoutSelectBoxWrap>
                <SelectBox
                  value={setting.navLayout}
                  options={Object.values(NavLayout)}
                  direction={false}
                  handleChange={handleNavLayoutSelect}
                  getDisplayText={getNavLayoutLabel}
                />
              </NavLayoutSelectBoxWrap>
            </ExposureRow>
          </SubWrapper>
        )}
        <SubWrapper>
          <SubTitle>{getSettingsMessage('SETTINGS_SHARE')}
            <SubExplain>{getSettingsMessage('SHARE_EXPLAIN')}</SubExplain>
          </SubTitle>
          <MoleculeDivBtn
            value={getShareValue()}
            defaultValue={"NONE"}
            handleClick={handleChangeShare}
            DivChangeStyle={deviceType === DeviceType.MOBILE ? SubValueChangeMobile : SubValueChangeMobile}
            DivStyle={SubValue}
          />
        </SubWrapper>
        <SubWrapper>
          <SubTitle>{getSettingsMessage('SETTINGS_SEARCH')}
            <SubExplain>{getSettingsMessage('SEARCH_EXPLAIN')}</SubExplain>
          </SubTitle>
          <MoleculeDivBtn
            value={getSearchEngineValue()}
            defaultValue={"NONE"}
            handleClick={handleChangeSearch}
            DivChangeStyle={deviceType === DeviceType.MOBILE ? SubValueChangeMobile : SubValueChangeMobile}
            DivStyle={SubValue}
          />
        </SubWrapper>
      </SubContainer>
    </PageSettingComp>
  );
}

const PageSettingComp = styled.section`
  width: clamp(320px, 65%, 650px);
  height: fit-content;
  display: flex;
  flex-direction: column;
  margin-bottom: 30px;
`;

const SubExplain = styled.div`
  width: fit-content;
  padding-top: 4px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_04};
`;

/** MoleculeDivToggle 과 동일한 한 줄: 왼쪽 라벨, 오른쪽 컨트롤 */
const ExposureRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  ${({ theme }) => theme.typography.Body_04};
`;

const ExposureCurrentLabel = styled.div`
  width: fit-content;
  padding: 6px 0;
  color: ${({ theme }) => theme.colors.color_Gray_01};
  user-select: none;
`;

const ExposureSelectBoxWrap = styled.div`
  width: clamp(90px, 8vw, 110px);
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const NavLayoutSelectBoxWrap = styled.div`
  width: clamp(110px, 11vw, 150px);
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

export default PageSetting;
